import XLSX from 'xlsx';
import React, { useState } from 'react';
import { useLocation } from 'react-router';
import { Form, Message } from 'semantic-ui-react';
import { DateInput } from 'semantic-ui-calendar-react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import moment from 'moment';

import GET_REPORTING_DROPDOWN_DATA from '../../../graphql/queries/getReportingDropdownData';
import DOWNLOAD_REPORT from '../../../graphql/mutations/downloadReport';
import GET_ENTRIES from '../../../graphql/queries/getEntries';
import { useManipulators } from '../../../hooks/useManipulators';
import { EntriesTable } from '../../../components/EntriesTable';

function mapEntityToDropdownOptions(entities = [], suffix) {
  const defaultOption = {
    text: `All ${suffix}`,
    value: 0,
  };

  return [defaultOption].concat(entities.map(entity => ({
    key: entity.id,
    text: entity.name,
    value: entity.id,
  })));
}

function firstDateOfMonth() {
  return moment().startOf('month').format('DD.MM.YYYY');
}

function lastDateOfMonth() {
  return moment().endOf('month').format('DD.MM.YYYY');
}

export const GeneralReporting = () => {
  const { reportOn } = useLocation();

  const [mutationError, setMutationError] = useState(false);
  const [mutationLoading, setMutationLoading] = useState(false);
  const [formData, setFormData] = useState({
    start: firstDateOfMonth(),
    end: lastDateOfMonth(),
    project: reportOn && reportOn.project ? reportOn.project : 0,
    worker: reportOn && reportOn.worker ? reportOn.worker : 0,
    categoryId: 0,
    work: reportOn && reportOn.work ? reportOn.work : 0,
    device: reportOn && reportOn.device ? reportOn.device : 0,
  });

  const { data = {}, loading } = useQuery(GET_REPORTING_DROPDOWN_DATA);

  // GraphQL expects Float or Int explicitly. Value of '25' string from Semantic UI dropdown 
  // needs to be parsed if the backend schema expects an Int.
  const numericFormData = {
    ...formData,
    project: formData.project ? parseInt(formData.project, 10) : 0,
    worker: formData.worker ? parseInt(formData.worker, 10) : 0,
    categoryId: formData.categoryId ? parseInt(formData.categoryId, 10) : 0,
    work: formData.work ? parseInt(formData.work, 10) : 0,
    device: formData.device ? parseInt(formData.device, 10) : 0,
  };

  const [downloadReportMutation] = useMutation(DOWNLOAD_REPORT, {
    variables: {
      data: numericFormData,
    },
  });

  async function downloadReport() {
    setMutationLoading(true);

    try {
      const { data } = await downloadReportMutation();

      const worksheet = XLSX.utils.json_to_sheet(JSON.parse(data.downloadReport));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, worksheet, 'Report sheet');

      XLSX.writeFile(wb, `REPORT_${moment().format('DDMMYYYY-HHmm')}.xlsx`)

      setMutationError(null);
      setMutationLoading(false);
    } catch (e) {
      setMutationLoading(false);

      if (e.networkError) {
        setMutationError('Network error. Check your connection.');
      } else if (e.graphQLErrors) {
        setMutationError(e.graphQLErrors[0].message);
      } else {
        setMutationError(e.message);
      }
    }
  }

  const { data: entriesData = {}, loading: entriesLoading } = useQuery(GET_ENTRIES, {
    fetchPolicy: 'cache-and-network',
    variables: { 
      filters: numericFormData,
      orderBy: { by: 'start' }
    },
  });

  function handleChange(e, { name, value }) {
    setFormData({
      ...formData,
      [name]: value,
    });
  }

  const errorMessage = mutationError ? (
    <Message negative>
      <Message.Header>Error has occured</Message.Header>
      <p>{ mutationError }</p>
    </Message>
  ) : null;

  const filteredCategories = data.getAllProjectCategories 
    ? (numericFormData.project !== 0 
        ? data.getAllProjectCategories.filter(c => parseInt(c.projectId, 10) === numericFormData.project) 
        : data.getAllProjectCategories) 
    : [];

  const getDescendantCategoryIds = (categories, parentId) => {
    let ids = [parentId];
    const children = categories.filter(c => c.parentId != null && parseInt(c.parentId, 10) === parentId);
    for (const child of children) {
      ids = ids.concat(getDescendantCategoryIds(categories, parseInt(child.id, 10)));
    }
    return ids;
  };

  let filteredWorks = data.works || [];
  
  // 1. Filter by Project if selected
  if (numericFormData.project !== 0 && data.projects) {
    const selectedProject = data.projects.find(p => parseInt(p.id, 10) === numericFormData.project);
    if (selectedProject && selectedProject.works) {
      const projectWorkIds = selectedProject.works.map(w => parseInt(w.id, 10));
      filteredWorks = filteredWorks.filter(w => projectWorkIds.includes(parseInt(w.id, 10)));
    } else {
      filteredWorks = [];
    }
  }

  // 2. Filter by Work Category if selected (cascades on top of Project filter)
  if (numericFormData.categoryId !== 0 && data.getAllProjectCategories && data.projects) {
    const selectedCat = data.getAllProjectCategories.find(c => parseInt(c.id, 10) === numericFormData.categoryId);
    if (selectedCat) {
      const allowedCategoryIds = getDescendantCategoryIds(data.getAllProjectCategories, numericFormData.categoryId);
      const project = data.projects.find(p => parseInt(p.id, 10) === parseInt(selectedCat.projectId, 10));
      if (project && project.works) {
        // Extract works that are assigned to any of the allowed category ids
        const allowedWorkIds = project.works
          .filter(w => w.categoryId != null && allowedCategoryIds.includes(parseInt(w.categoryId, 10)))
          .map(w => parseInt(w.id, 10));
        
        filteredWorks = filteredWorks.filter(w => allowedWorkIds.includes(parseInt(w.id, 10)));
      } else {
        filteredWorks = [];
      }
    } else {
      filteredWorks = [];
    }
  }

  return (
    <React.Fragment>
      { errorMessage }
      <Form
        loading={loading}
        size='small'
        autoComplete='off'
        id='modal-form'
      >
        <Form.Group widths='equal'>
          <DateInput
            name='start'
            label='Start date'
            value={formData.start}
            animation='none'
            dateFormat='DD.MM.YYYY'
            closable={true}
            popupPosition='bottom right'
            preserveViewMode={false}
            onChange={handleChange}
          />

          <DateInput
            name='end'
            label='End date'
            value={formData.end}
            animation='none'
            dateFormat='DD.MM.YYYY'
            minDate={formData.start}
            closable={true}
            popupPosition='bottom right'
            preserveViewMode={false}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group widths='equal'>
          <Form.Dropdown
            search
            selection
            name='project'
            label='Project'
            value={formData.project}
            options={mapEntityToDropdownOptions(data.projects, 'projects')}
            onChange={handleChange}
          />

          <Form.Dropdown
            search
            selection
            name='worker'
            label='Worker'
            value={formData.worker}
            options={mapEntityToDropdownOptions(data.workers, 'workers')}
            onChange={handleChange}
          />

          <Form.Dropdown
            search
            selection
            name='categoryId'
            label='Work Category'
            value={formData.categoryId}
            options={mapEntityToDropdownOptions(filteredCategories, 'categories')}
            onChange={handleChange}
          />

          <Form.Dropdown
            search
            selection
            name='work'
            label='Work'
            value={formData.work}
            options={mapEntityToDropdownOptions(filteredWorks, 'works')}
            onChange={handleChange}
          />

          <Form.Dropdown
            search
            selection
            name='device'
            label='Device'
            value={formData.device}
            options={mapEntityToDropdownOptions(data.devices, 'devices')}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Button
          color='teal'
          icon='save'
          content='Download'
          loading={mutationLoading}
          disabled={mutationLoading}
          onClick={downloadReport}
        />
      </Form>

      <EntriesTable
        entriesData={entriesData}
        entriesLoading={entriesLoading}
      />
    </React.Fragment>
  );
};
